import { Stack, Text } from "@certn/cascadia";
import { InferClientPortalAPI } from "@certn/api";
import { LinkButton, SummaryMessage } from "@certn/cascadia-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { OverdueBillingPopup } from "./OverdueBillingPopup";
import {
  LEGACY_BANNER,
  OPEN_INVOICES_PATH,
  POPUP,
  SCHEDULE_BANNER,
} from "./utils";

type BillingNoticesData = NonNullable<
  InferClientPortalAPI<"/api/internal/client-portal/customer-accounts/billing-notices/">
>;
type OverdueProfile = BillingNoticesData["overdue_profiles"][number];
type BlockingProfile = BillingNoticesData["blocking_profiles"][number];

interface Props {
  overdueProfiles: OverdueProfile[];
  blockingProfiles?: BlockingProfile[];
}

export function OverdueBillingBanner({
  overdueProfiles,
  blockingProfiles = [],
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const blockingProfileIds = new Set(
    blockingProfiles.map((profile) => profile.billing_profile_id)
  );
  const bannerNotices = overdueProfiles.filter(
    (profile) =>
      profile.surface !== POPUP &&
      !blockingProfileIds.has(profile.billing_profile_id)
  );
  const scheduleBanners = bannerNotices.filter(
    (profile) => profile.surface === SCHEDULE_BANNER
  );
  const legacyBanners = bannerNotices.filter(
    (profile) => !profile.surface || profile.surface === LEGACY_BANNER
  );
  const openInvoices = () => navigate(OPEN_INVOICES_PATH);

  return (
    <>
      {blockingProfiles.length > 0 && (
        <BlockingBanner
          blockingProfiles={blockingProfiles}
          onViewInvoices={openInvoices}
        />
      )}
      {scheduleBanners.map((profile) => (
        <SummaryMessage key={profile.billing_profile_id} type="warning">
          <div>
            <Stack gap="space-2" orientation="horizontal">
              <Text color="text-base" style="global/heading-small">
                {profile.title}
              </Text>
            </Stack>
            {profile.message && (
              <Stack gap="space-2" orientation="horizontal">
                <Text color="text-weak" style="global/body-secondary">
                  {profile.message}
                </Text>
              </Stack>
            )}
            <Stack gap="space-2" orientation="horizontal">
              <LinkButton
                linkStyle="monochrome"
                textStyle="global/body-secondary"
                onPress={openInvoices}
                testID={`overdue-schedule-banner-${profile.billing_profile_id}`}
              >
                {t("Pay now")}
              </LinkButton>
            </Stack>
          </div>
        </SummaryMessage>
      ))}
      {legacyBanners.length > 0 && (
        <SummaryMessage type="warning">
          <div>
            <Stack gap="space-2" orientation="horizontal">
              <Text color="text-base" style="global/heading-small">
                {t("You have overdue invoices")}
              </Text>
            </Stack>
            <Stack gap="space-2" orientation="horizontal">
              <Text color="text-weak" style="global/body-secondary">
                {legacyBannerMessage(legacyBanners, t)}
              </Text>
            </Stack>
            <Stack gap="space-2" orientation="horizontal">
              <LinkButton
                linkStyle="monochrome"
                textStyle="global/body-secondary"
                onPress={openInvoices}
                testID="overdue-billing-banner-link"
              >
                {t("View invoices")}
              </LinkButton>
            </Stack>
          </div>
        </SummaryMessage>
      )}
      <OverdueBillingPopup profiles={overdueProfiles} />
    </>
  );
}

function BlockingBanner({
  blockingProfiles,
  onViewInvoices,
}: {
  blockingProfiles: BlockingProfile[];
  onViewInvoices: () => void;
}) {
  const { t } = useTranslation();
  const totalBlocking = blockingProfiles.length;
  const totalUnpaid = blockingProfiles.reduce(
    (sum, profile) => sum + profile.unpaid_invoice_count,
    0
  );
  const isCollectionsHold = blockingProfiles.some(
    (profile) => profile.reason === "COLLECTIONS"
  );
  const areAllCollectionsHolds = blockingProfiles.every(
    (profile) => profile.reason === "COLLECTIONS"
  );

  const getBlockingMessage = () => {
    if (totalBlocking === 1 && isCollectionsHold) {
      const profile = blockingProfiles[0];
      if (profile.total_outstanding_amount === null) {
        return t(
          "Your service has been paused for {{profileName}} due to non-payment. Submit payment for the full overdue balance to restore ordering access.",
          { profileName: profile.billing_profile_name }
        );
      }
      return t(
        "Your service has been paused for {{profileName}} due to non-payment ({{amount}} {{currency}} overdue). Submit payment for the full overdue balance to restore ordering access.",
        {
          profileName: profile.billing_profile_name,
          amount: profile.total_outstanding_amount,
          currency: profile.currency,
        }
      );
    }
    if (areAllCollectionsHolds) {
      return t(
        "Your service has been paused for {{profileCount}} billing profiles due to non-payment. Submit payment for the full overdue balances to restore ordering access.",
        { profileCount: totalBlocking }
      );
    }
    if (totalBlocking === 1) {
      const profile = blockingProfiles[0];

      return t(
        "There are currently {{count}} unpaid invoice(s) for {{profileName}} totaling {{amount}} {{currency}}. Please pay the balance to resume ordering immediately.",
        {
          count: totalUnpaid,
          profileName: profile.billing_profile_name,
          amount: profile.total_outstanding_amount,
          currency: profile.currency,
        }
      );
    }

    return t(
      "There are currently {{invoiceCount}} unpaid invoice(s) across {{profileCount}} billing profiles. Please pay the balances to resume ordering immediately.",
      {
        invoiceCount: totalUnpaid,
        profileCount: totalBlocking,
      }
    );
  };

  return (
    <SummaryMessage type="danger">
      <div>
        <Stack gap="space-2" orientation="horizontal">
          <Text color="text-base" style="global/heading-small">
            {isCollectionsHold ? t("Service paused") : t("Ordering is paused")}
          </Text>
        </Stack>
        <Stack gap="space-2" orientation="horizontal">
          <Text color="text-weak" style="global/body-secondary">
            {getBlockingMessage()}
          </Text>
        </Stack>
        <Stack gap="space-2" orientation="horizontal">
          <LinkButton
            linkStyle="monochrome"
            textStyle="global/body-secondary"
            onPress={onViewInvoices}
            testID="blocking-billing-banner-link"
          >
            {t("View invoices")}
          </LinkButton>
        </Stack>
      </div>
    </SummaryMessage>
  );
}

function legacyBannerMessage(profiles: OverdueProfile[], t: TFunction) {
  const totalProfiles = profiles.length;
  const totalInvoices = profiles.reduce(
    (sum, profile) => sum + profile.overdue_invoice_count,
    0
  );
  if (totalProfiles === 1) {
    const profile = profiles[0];
    if (totalInvoices === 1) {
      return t(
        "1 invoice is overdue for {{profileName}} ({{amount}} {{currency}}, {{days}} days overdue).",
        {
          profileName: profile.billing_profile_name,
          amount: profile.total_overdue_amount,
          currency: profile.currency,
          days: profile.days_overdue,
        }
      );
    }
    return t(
      "{{count}} invoices are overdue for {{profileName}} ({{amount}} {{currency}}, {{days}} days overdue).",
      {
        count: totalInvoices,
        profileName: profile.billing_profile_name,
        amount: profile.total_overdue_amount,
        currency: profile.currency,
        days: profile.days_overdue,
      }
    );
  }
  return t(
    "{{invoiceCount}} invoices are overdue across {{profileCount}} billing profiles.",
    {
      invoiceCount: totalInvoices,
      profileCount: totalProfiles,
    }
  );
}
